<script setup>
import {Head, useForm} from "@inertiajs/inertia-vue3";
import {computed, onUnmounted, ref} from "vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import Banner from "@/Components/Banner.vue";
import {useTenantTextRules} from "@/helpers";

const props = defineProps({
  pid: {type: Number, required: true},
  postActionUrl: {type: String, required: true},
  projectName: {type: String, default: ""},
  isClosed: {type: Boolean, default: false},
});

const {resolveTextRule} = useTenantTextRules();
const fileInput = ref(null);
let previewIdSeq = 0;

const previewItems = ref([]);

const revokeAllPreviews = () => {
  for (const item of previewItems.value) {
    URL.revokeObjectURL(item.objectUrl);
  }
  previewItems.value = [];
};

function createPreviewItem(file) {
  return {
    id: `${Date.now()}-${++previewIdSeq}`,
    file,
    name: file.name,
    objectUrl: URL.createObjectURL(file),
    previewError: false,
  };
}

const onFileInputChange = () => {
  const files = fileInput.value?.files;
  if (!files?.length) {
    return;
  }
  for (const file of Array.from(files)) {
    previewItems.value.push(createPreviewItem(file));
  }
  fileInput.value.value = "";
};

const openFilePicker = () => {
  fileInput.value?.click();
};

const removePreviewAt = (index) => {
  const item = previewItems.value[index];
  if (item) {
    URL.revokeObjectURL(item.objectUrl);
    previewItems.value.splice(index, 1);
  }
};

onUnmounted(() => {
  revokeAllPreviews();
});

const form = useForm({
  media: [],
});

const title = () =>
  resolveTextRule(
    "project.public_upload.page_title",
    "Upload Images",
    "project.public_upload"
  );

const intro = () =>
  resolveTextRule(
    "project.public_upload.intro",
    "Upload clear images from each angle below.",
    "project.public_upload"
  );

const labels = [
  () =>
    resolveTextRule(
      "project.public_upload.slot_vehicle_front",
      "Front of Rental Equipment",
      "project.public_upload"
    ),
  () =>
    resolveTextRule(
      "project.public_upload.slot_driver_side",
      "Right Side of Rental Equipment",
      "project.public_upload"
    ),
  () =>
    resolveTextRule(
      "project.public_upload.slot_passenger_side",
      "Left side of Rental Equipment",
      "project.public_upload"
    ),
  () =>
    resolveTextRule(
      "project.public_upload.slot_vehicle_back",
      "Back of Rental Equipment",
      "project.public_upload"
    ),
  () =>
    resolveTextRule(
      "project.public_upload.slot_interior",
      "Photo of Hours and Fuel Level",
      "project.public_upload"
    ),
  () =>
    resolveTextRule(
      "project.public_upload.slot_optional_images",
      "Optional images",
      "project.public_upload"
    ),
];

const submitLabel = () =>
  resolveTextRule(
    "project.public_upload.submit",
    "Upload Images",
    "project.public_upload"
  );

const previewHeading = () =>
  resolveTextRule(
    "project.public_upload.preview_heading",
    "Preview",
    "project.public_upload"
  );

const showPreviewGrid = computed(() => previewItems.value.length > 0);

const addPhotoButtonLabel = computed(() =>
  previewItems.value.length === 0
    ? resolveTextRule(
        "project.public_upload.add_photos",
        "Add photos",
        "project.public_upload"
      )
    : resolveTextRule(
        "project.public_upload.add_another_photo",
        "Add another photo",
        "project.public_upload"
      )
);

const removePhotoLabel = () =>
  resolveTextRule(
    "project.public_upload.remove_photo",
    "Remove",
    "project.public_upload"
  );

const uploadMedia = () => {
  if (!previewItems.value.length) {
    return;
  }
  form.media = previewItems.value.map((item) => item.file);
  form.post(props.postActionUrl, {
    forceFormData: true,
    preserveScroll: true,
    onSuccess: () => {
      revokeAllPreviews();
      if (fileInput.value) {
        fileInput.value.value = "";
      }
    },
    onFinish: () => {
      form.reset();
    },
  });
};

const onPreviewError = (item) => {
  item.previewError = true;
};
</script>

<template>
  <div>
    <Head :title="title()" />

    <div class="min-h-screen bg-gray-100 py-6 px-4 sm:px-6">
      <div class="max-w-lg mx-auto bg-white shadow-md rounded-lg p-6 space-y-6">
        <div>
          <h1 class="text-xl font-semibold text-gray-900" v-text="title()"></h1>
          <p v-if="projectName" class="mt-1 text-sm text-gray-600">
            {{ intro() }}
            <span class="font-medium block mt-1">{{ projectName }}</span>
          </p>
          <p v-else class="mt-1 text-sm text-gray-600" v-text="intro()"></p>
          <p v-if="isClosed" class="mt-2 text-sm text-amber-700">
            {{
              resolveTextRule(
                "project.public_upload.closed_notice",
                "This project is closed; uploads still attach for records.",
                "project.public_upload"
              )
            }}
          </p>
        </div>

        <div class="space-y-3 border-t border-gray-200 pt-4">
          <p class="text-sm font-medium text-gray-700">
            {{
              resolveTextRule(
                "project.public_upload.checklist_heading",
                "Include images for:",
                "project.public_upload"
              )
            }}
          </p>
          <ul class="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li v-for="(labelFn, idx) in labels" :key="idx" v-text="labelFn()"></li>
          </ul>
        </div>

        <div class="space-y-3">
          <p class="block text-sm font-medium text-gray-700">
            {{
              resolveTextRule(
                "project.public_upload.files_label",
                "Choose images",
                "project.public_upload"
              )
            }}
          </p>
          <p class="text-xs text-gray-500">
            {{
              resolveTextRule(
                "project.public_upload.add_photo_hint",
                "Add each photo, then use “Add another photo” until you are ready to upload.",
                "project.public_upload"
              )
            }}
          </p>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.heic,.heif"
            class="sr-only"
            multiple
            @change="onFileInputChange"
          />
          <SecondaryButton type="button" class="w-full sm:w-auto" @click="openFilePicker">
            {{ addPhotoButtonLabel }}
          </SecondaryButton>
        </div>

        <div v-if="showPreviewGrid" class="space-y-2 border-t border-gray-200 pt-4">
          <p class="text-sm font-medium text-gray-800">{{ previewHeading() }}</p>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div
              v-for="(item, idx) in previewItems"
              :key="item.id"
              class="relative aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50"
            >
              <button
                type="button"
                class="absolute top-1 right-1 z-10 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
                :aria-label="removePhotoLabel()"
                @click="removePreviewAt(idx)"
              >
                ×
              </button>
              <img
                v-show="!item.previewError"
                :src="item.objectUrl"
                :alt="item.name"
                class="w-full h-full object-cover"
                @error="onPreviewError(item)"
              />
              <div
                v-if="item.previewError"
                class="flex h-full min-h-[5rem] items-center justify-center p-2 text-center text-xs text-gray-600"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <PrimaryButton
            type="button"
            :disabled="form.processing || !previewItems.length"
            @click="uploadMedia"
          >
            {{ submitLabel() }}
          </PrimaryButton>
        </div>
      </div>
    </div>

    <div class="max-w-3xl sticky bottom-0 z-[100] mx-auto w-full px-2">
      <Banner />
    </div>
  </div>
</template>
